import { apiClient } from './apiClient';
import { getApiData, getProp } from '../utils/apiResponse';

export interface TeamViewerLicenseSummary {
  accountName: string;
  licensedUsers: number;
  totalSeats?: number;
  availableSeats?: number;
  retrievedAtUtc: string;
}

export interface TeamViewerLicenseUser {
  id: string;
  name: string;
  email?: string;
}

export interface TeamViewerSettings {
  corporateLicenseChannels: number;
  retrievedAtUtc: string;
}

export interface TeamViewerChannelUsageSummary {
  maxChannels: number;
  usedChannels: number;
  availableChannels: number;
  source?: string;
  warning?: string;
  retrievedAtUtc: string;
}

export interface TeamViewerSession {
  code: string;
  state?: string;
  online?: boolean;
  groupId?: string;
  supportSessionType?: string;
}

export interface UpdateTeamViewerSettingsRequest {
  corporateLicenseChannels: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const teamViewerLicenseService = {
  async getSummary(): Promise<TeamViewerLicenseSummary> {
    const response =
      await apiClient.get<ApiResponse<TeamViewerLicenseSummary>>('/teamviewer/users');
    return getApiData<TeamViewerLicenseSummary>(response, 'loading TeamViewer license summary');
  },

  async getSettings(): Promise<TeamViewerSettings> {
    const response = await apiClient.get<ApiResponse<TeamViewerSettings>>(
      '/teamviewer/users/settings'
    );
    return getApiData<TeamViewerSettings>(response, 'loading TeamViewer settings');
  },

  async updateSettings(request: UpdateTeamViewerSettingsRequest): Promise<TeamViewerSettings> {
    const response = await apiClient.put<ApiResponse<TeamViewerSettings>>(
      '/teamviewer/users/settings',
      request
    );
    return getApiData<TeamViewerSettings>(response, 'updating TeamViewer settings');
  },

  async getUsers(): Promise<TeamViewerLicenseUser[]> {
    const response =
      await apiClient.get<ApiResponse<TeamViewerLicenseUser[]>>('/teamviewer/users/list');
    const data = getApiData<any>(response, 'loading TeamViewer licensed users');

    if (Array.isArray(data)) {
      return data as TeamViewerLicenseUser[];
    }

    const value = getProp(data, 'value');
    if (Array.isArray(value)) {
      return value as TeamViewerLicenseUser[];
    }

    return [];
  },

  async getChannelUsage(): Promise<TeamViewerChannelUsageSummary> {
    const response = await apiClient.get<ApiResponse<TeamViewerChannelUsageSummary>>(
      '/teamviewer/users/channels'
    );
    return getApiData<TeamViewerChannelUsageSummary>(response, 'loading TeamViewer channel usage');
  },

  async getSessions(): Promise<TeamViewerSession[]> {
    const response = await apiClient.get<ApiResponse<TeamViewerSession[]>>(
      '/teamviewer/users/sessions'
    );
    return getApiData<TeamViewerSession[]>(response, 'loading TeamViewer sessions');
  },
};
